import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { NotificationCategory, NotificationDto } from '@rw/shared';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { Button } from '../../components/ui';
import { qk, useNotifications } from '../../hooks/queries';
import { api } from '../../lib/api';
import { setAppBadge } from '../../lib/app-badge';
import { timeAgo } from '../../lib/format';
import { useT } from '../../i18n';

const ICON: Record<NotificationCategory, string> = {
  social: '👏',
  territory: '⚔️',
  clubs: '🛡️',
  events: '🏁',
  imports: '⌚',
  achievements: '🏅',
};

/** The bell's list: everything that happened to you, newest first. Opening it marks all as read. */
export function NotificationsPage() {
  const navigate = useNavigate();
  const t = useT();
  const qc = useQueryClient();
  const q = useNotifications();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = q.data?.pages[0]?.unread ?? 0;
  // What was new when the page opened stays highlighted after it is marked read.
  const fresh = useRef<Set<string> | null>(null);
  if (fresh.current === null && q.data) fresh.current = new Set(items.filter((n) => !n.read).map((n) => n.id));

  const markAll = useMutation({
    mutationFn: () => api<{ unread: number }>('/notifications/read', { method: 'POST', body: {} }),
    onSuccess: (r) => {
      qc.setQueryData(qk.notificationsUnread, r);
      setAppBadge(r.unread);
    },
  });
  const { mutate } = markAll;
  useEffect(() => {
    if (unread > 0) mutate();
  }, [unread, mutate]);

  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        ← Back
      </button>
      <h1 className="font-display text-4xl font-extrabold uppercase">{t('Notifications')}</h1>
      {q.isPending ? (
        <LoadingState />
      ) : q.isError && !items.length ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon="🔔" title={t('Nothing yet')} body={t('Kudos, comments, lost sectors, club and event news will show up here.')} />
      ) : (
        <ul className="mt-4 space-y-2" data-testid="notifications">
          {items.map((n) => (
            <Item key={n.id} n={n} isNew={!n.read || !!fresh.current?.has(n.id)} onOpen={() => navigate(n.url)} />
          ))}
        </ul>
      )}
      {q.hasNextPage ? (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          {t(q.isFetchingNextPage ? 'Loading…' : 'Older')}
        </Button>
      ) : null}
    </Page>
  );
}

function Item({ n, isNew, onOpen }: { n: NotificationDto; isNew: boolean; onOpen: () => void }) {
  return (
    <li>
      <button
        onClick={onOpen}
        className={`flex w-full items-start gap-3 rounded-xl p-3 text-left text-sm ${isNew ? 'bg-ink-800 ring-1 ring-volt/40' : 'bg-ink-850'}`}
        data-testid="notification-item"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-ink-900 text-lg" aria-hidden="true">
          {ICON[n.category] ?? '🔔'}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block ${isNew ? 'font-bold' : 'font-semibold text-ink-200'}`}>{n.title}</span>
          {n.body ? <span className="mt-0.5 block text-ink-400">{n.body}</span> : null}
          <span className="mt-1 block text-xs text-ink-500">{timeAgo(n.createdAt)}</span>
        </span>
        {isNew ? <span className="mt-1.5 size-2 shrink-0 rounded-full bg-volt" aria-label="new" /> : null}
      </button>
    </li>
  );
}
